import React from "react";
import {
  Activity,
  ClipboardCheck,
  FileText,
  Lightbulb,
  MessageCircle,
  Mic,
  Search,
  Smile,
  Users,
} from "lucide-react";
import CapabilityCard from "../CapabilityCard/CapabilityCard";
import SectionHeading from "../SectionHeading/SectionHeading";

const CARDS = [
  {
    icon: Mic,
    title: "Live Conversation Capture",
    description:
      "Every customer interaction is recorded automatically — built into the rep's mobile workflow, on every appointment.",
    tag: "Every call recorded",
  },
  {
    icon: FileText,
    title: "Full Call Transcripts",
    description:
      "Word-for-word transcripts of every interaction. Nothing missed — no objection, no commitment, no detail.",
    tag: "Searchable & timestamped",
  },
  {
    icon: ClipboardCheck,
    title: "Script Adherence Scoring",
    description:
      "Every call scored against your CertainPath sales process. See which steps were hit, which were missed, and where execution drifts from the playbook.",
    tag: "Measurable execution",
  },
  {
    icon: MessageCircle,
    title: "Objection Handling Evaluation",
    description:
      "Specific feedback on how each objection was handled — so coaching becomes concrete, not “work on your objections.”",
    tag: "Specific feedback",
  },
  {
    icon: Activity,
    title: "Tone & Confidence Analysis",
    description:
      "How something is said matters as much as what's said. FieldCoach analyzes vocal delivery, pacing, and confidence patterns.",
    tag: "Beyond just words",
  },
  {
    icon: Smile,
    title: "Sentiment Tracking",
    description:
      "Customer and employee sentiment scored on every call. Spot disengagement and missed connection moments early.",
    tag: "Customer + employee",
  },
  {
    icon: Users,
    title: "Voice ID Speaker Separation",
    description:
      "Voice ID identifies your rep across recordings — eliminating “who said what” disputes and sharpening coaching accuracy.",
    tag: "Crystal-clear attribution",
  },
  {
    icon: Lightbulb,
    title: "AI Coaching Insights",
    description:
      "Qualitative, context-aware recommendations with timestamps. Managers know exactly what to address in the next 1:1.",
    tag: "Context-aware",
  },
  {
    icon: Search,
    title: "Behavior Pattern Detection",
    description:
      "Surface drift before it impacts revenue: shortened discovery, increased discounting, weakened closes — caught early.",
    tag: "Catch drift early",
  },
];

const CapabilityBlock: React.FC = () => (
  <section
    className="relative overflow-hidden bg-secondary py-24"
    id="capabilities"
  >
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 bg-[radial-gradient(40%_50%_at_80%_10%,rgba(178,0,48,.16),transparent_60%),radial-gradient(35%_45%_at_10%_90%,rgba(255,200,87,.06),transparent_60%)]"
    />

    <div className="relative z-10 mx-auto max-w-7xl px-6">
      <div className="mx-auto mb-14 max-w-3xl text-center">
        <SectionHeading onDark>Make field performance visible.</SectionHeading>
        <p className="mx-auto mt-5 max-w-2xl text-lg font-light leading-relaxed text-white/75">
          Most organizations invest heavily in training, coaching, and defined
          processes. Yet execution still{" "}
          <em className="font-extrabold not-italic text-white">varies</em> —
          because managers can&rsquo;t see what&rsquo;s actually happening in
          the field.{" "}
          <span className="font-bold text-gold">
            FieldCoach turns every call into measurable, coachable, and
            accountable behavior.
          </span>
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {CARDS.map((card) => (
          <CapabilityCard
            description={card.description}
            icon={card.icon}
            key={card.title}
            tag={card.tag}
            title={card.title}
          />
        ))}
      </div>
    </div>
  </section>
);

export default CapabilityBlock;
